import {
  ArrowForward,
  VerifiedOutlined,
} from '@mui/icons-material';
import { Box, Button, Chip, Container, Grid, Stack, Typography } from '@mui/material';

import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ProblemSolutionRow from '../components/ProblemSolutionRow';
import ProcessWorkflow from '../components/ProcessWorkflow';
import UseCasesSection from '../components/UseCasesSection';

export default function MainPage() {
  const navigate = useNavigate();
  const theme = useTheme();

  const logos = [
    { src: '/assets/afrl_logo.webp', link: 'https://www.afrl.af.mil/', label: 'Air Force Research Laboratory' },
    { src: '/assets/pratt_whitney_logo.webp', link: 'https://www.prattwhitney.com/', label: 'Pratt & Whitney' },
    { src: '/assets/aflcmc_logo.webp', link: 'https://www.aflcmc.af.mil/', label: 'Air Force Life Cycle Management Center' },
  ];



  return (
    <Box sx={{ overflow: 'hidden', bgcolor: 'background.default' }}>
      <Box
        component="section"
        sx={{
          position: 'relative',
          color: 'white',
          minHeight: { xs: 'calc(88vh - 68px)', md: 'calc(88vh - 78px)' },
          display: 'flex',
          alignItems: 'center',
          pb: { xs: 8, md: 10 },
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: 'linear-gradient(90deg, rgba(8, 21, 27, 0.92) 0%, rgba(8, 21, 27, 0.74) 42%, rgba(8, 21, 27, 0.32) 100%)',
            zIndex: 1,
          },
        }}
      >
        <Box
          component="img"
          src="/main-page-dark.webp"
          alt="Finite element model visualized as a scan-based digital twin"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={7} lg={6}>
              <Chip
                label="Commercial mesh morphing software"
                sx={{
                  mb: 3,
                  color: 'white',
                  bgcolor: alpha(theme.palette.secondary.main, 0.78),
                  fontWeight: 700,
                }}
              />
              <Typography variant="h1" sx={{ fontSize: { xs: '3.3rem', sm: '4.5rem', md: '6rem' }, lineHeight: 0.92, mb: 3 }}>
                FEMORPH
              </Typography>
              <Typography variant="h4" component="p" sx={{ maxWidth: 720, fontWeight: 650, lineHeight: 1.18, mb: 2 }}>
                Adapt any finite element model to any geometry.
              </Typography>
              <Typography variant="h6" component="p" sx={{ maxWidth: 650, color: 'rgba(255,255,255,0.82)', fontWeight: 400, lineHeight: 1.55, mb: 4 }}>
                Whether conforming to CAD design changes, as-manufactured 3D scans, or CMM measurements, FEMORPH morphs your existing mesh while preserving topology and element quality — across design, production, and sustainment.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button variant="contained" size="large" endIcon={<ArrowForward />} onClick={() => navigate('/features')}>
                  Explore features
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.6)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}
                  onClick={() => navigate('/case-studies')}
                >
                  View case studies
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>


      <Box component="section" sx={{ bgcolor: '#172126', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 4, md: 5 } }}>
          <Grid container spacing={2.5}>

            <Grid item xs={12} md={4}>
              <Box sx={{ borderLeft: '3px solid', borderColor: 'secondary.main', pl: 2.5, py: 0.5 }}>
                <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.6)' }}>Open-source pedigree</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5, mb: 0.5 }}>
                  <Box component="img" src="/assets/pyansys.png" alt="PyAnsys" sx={{ height: 36, objectFit: 'contain' }} />
                  <Box component="img" src="/assets/pyvista_logo.png" alt="PyVista" sx={{ height: 36, objectFit: 'contain' }} />
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>From the developer of two of the most widely used open-source engineering Python libraries</Typography>
              </Box>
            </Grid>

            {[
              { label: 'Technology lineage',   value: 'Mission-ready',  detail: 'Developed at the Air Force Research Laboratory and hardened through real-world deployments across multiple aerospace customers' },
              { label: 'Mission-critical applications', value: 'Relied upon',  detail: 'Trusted for use on high-value assets — including active use in F-22 maintenance and repair workflows', image: '/assets/f22.jpg' },
            ].map((item) => (
              <Grid item xs={12} md={4} key={item.label}>
                <Box sx={{ borderLeft: '3px solid', borderColor: 'secondary.main', pl: 2.5, py: 0.5 }}>
                  <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.6)' }}>{item.label}</Typography>
                  {item.image ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ width: '75%' }}>
                        <Typography variant="h4" sx={{ color: 'white' }}>{item.value}</Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>{item.detail}</Typography>
                      </Box>
                      <Box component="img" src={item.image} alt="F-22 Raptor" sx={{ width: '25%', aspectRatio: '4/3', objectFit: 'cover', objectPosition: 'center', borderRadius: 0.5 }} />
                    </Box>
                  ) : (
                    <>
                      <Typography variant="h4" sx={{ color: 'white' }}>{item.value}</Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>{item.detail}</Typography>
                    </>
                  )}
                </Box>
              </Grid>
            ))}

          </Grid>
        </Container>
      </Box>

      <ProblemSolutionRow />

      <ProcessWorkflow />

      <UseCasesSection />

      <Box component="section" sx={{ bgcolor: '#172126', color: 'white' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 7, md: 9 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" sx={{ mb: 4 }}>
            <Box>
              <Typography variant="overline" sx={{ color: alpha('#fff', 0.7) }}>Validated across aerospace workflows</Typography>
              <Typography variant="h3" sx={{ mt: 1 }}>Trusted by Technology Leaders.</Typography>
            </Box>
            <Button variant="outlined" sx={{ color: 'white', borderColor: alpha('#fff', 0.5) }} startIcon={<VerifiedOutlined />} onClick={() => navigate('/case-studies')}>
                  View case studies
            </Button>
          </Stack>
          <Grid container spacing={2}>
            {logos.map((logo) => (
              <Grid item xs={12} sm={4} key={logo.label}>
                <Box
                  component="a"
                  href={logo.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={logo.label}
                  sx={{
                    height: 112,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'white',
                    border: '1px solid rgba(255,255,255,0.18)',
                    p: 2,
                  }}
                >
                  <Box component="img" src={logo.src} alt={logo.label} sx={{ maxWidth: '100%', maxHeight: 72, objectFit: 'contain' }} />
                </Box>
              </Grid>
            ))}
          </Grid>
          <Typography variant="h6" sx={{ mt: 3, color: alpha('#fff', 0.65), fontStyle: 'italic', fontWeight: 400 }}>
            …and other industry partners
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
